import React from 'react';
import { Scenario, ScenarioMetadata } from '../../types';
import { AssetField } from './AssetPickerModal';
import {
  AchievementsEditor,
  ChaptersEditor,
  MissionsEditor,
  SkillsEditor,
} from './MetadataListEditors';
import { JsonEditor } from './JsonEditor';
import { TextPlaceholdersEditor } from './TextPlaceholdersEditor';
import { isReleasedToProd } from './utils';
import styles from './scenario-studio.module.css';

const PEDA_VARIABLE_KEYS = ['peda_shields', 'peda_shields_max'] as const;

function getPedaVariable(metadata: ScenarioMetadata, key: typeof PEDA_VARIABLE_KEYS[number]): number {
  return metadata.initialVariables?.[key] ?? 0;
}

function patchPedaVariable(
  metadata: ScenarioMetadata,
  key: typeof PEDA_VARIABLE_KEYS[number],
  value: number
): ScenarioMetadata {
  return {
    ...metadata,
    initialVariables: {
      ...(metadata.initialVariables ?? {}),
      [key]: value,
    },
  };
}

function getOtherInitialVariables(metadata: ScenarioMetadata): Record<string, number> {
  const vars = metadata.initialVariables ?? {};
  return Object.fromEntries(
    Object.entries(vars).filter(([key]) => !PEDA_VARIABLE_KEYS.includes(key as typeof PEDA_VARIABLE_KEYS[number]))
  );
}

interface MetadataEditorProps {
  scenarioId: string;
  metadata: ScenarioMetadata;
  sceneIds: string[];
  scenes: Scenario['scenes'];
  onChange: (metadata: ScenarioMetadata) => void;
  onReleaseToProdChange?: (releaseToProd: boolean) => void | Promise<void>;
  releaseToProdSaving?: boolean;
}

export const MetadataEditor: React.FC<MetadataEditorProps> = ({
  scenarioId,
  metadata,
  sceneIds,
  scenes,
  onChange,
  onReleaseToProdChange,
  releaseToProdSaving = false,
}) => {
  const patch = (p: Partial<ScenarioMetadata>) => onChange({ ...metadata, ...p });
  const missionIds = (metadata.missions ?? []).map((m) => m.id);

  return (
    <div className={styles.panel}>
      <h3>Métadonnées</h3>
      <div className={styles.field}>
        <label>Titre</label>
        <input className={styles.input} value={metadata.title} onChange={(e) => patch({ title: e.target.value })} />
      </div>
      <AssetField
        scenarioId={scenarioId}
        label="Thumbnail"
        value={metadata.thumbnail}
        subdir="backgrounds"
        onChange={(thumbnail) => patch({ thumbnail })}
      />
      <div className={styles.field}>
        <label>Scène de départ</label>
        <select className={styles.select} value={metadata.startScene} onChange={(e) => patch({ startScene: e.target.value })}>
          {sceneIds.map((id) => (
            <option key={id} value={id}>{id}</option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <label>Version</label>
        <input className={styles.input} value={metadata.version ?? ''} onChange={(e) => patch({ version: e.target.value })} />
      </div>
      <div className={styles.field}>
        <label>Thème profil</label>
        <select
          className={styles.select}
          value={metadata.profileTheme ?? 'default'}
          onChange={(e) => patch({ profileTheme: e.target.value as 'default' | 'cyberpunk' })}
        >
          <option value="default">default</option>
          <option value="cyberpunk">cyberpunk</option>
        </select>
      </div>
      <div className={styles.field}>
        <label>
          <input
            type="checkbox"
            checked={isReleasedToProd(metadata)}
            disabled={releaseToProdSaving}
            onChange={(e) => onReleaseToProdChange?.(e.target.checked)}
          />
          {' '}Release to prod
        </label>
        <p className={styles.hint}>
          Si décoché, seuls les administrateurs voient ce scénario dans le catalogue. L&apos;enregistrement est
          automatique lors du changement.
        </p>
      </div>
      <div className={styles.phoneSection}>
        <h4>Boucliers PEDA</h4>
        <p className={styles.hint}>
          Le compteur en haut du jeu s&apos;affiche uniquement si « Boucliers max » est supérieur à 0.
        </p>
        <div className={styles.row}>
          <div className={styles.field} style={{ flex: 1 }}>
            <label>Boucliers initiaux</label>
            <input
              type="number"
              min={0}
              className={styles.input}
              value={getPedaVariable(metadata, 'peda_shields')}
              onChange={(e) =>
                onChange(patchPedaVariable(metadata, 'peda_shields', Math.max(0, parseInt(e.target.value, 10) || 0)))
              }
            />
          </div>
          <div className={styles.field} style={{ flex: 1 }}>
            <label>Boucliers max</label>
            <input
              type="number"
              min={0}
              className={styles.input}
              value={getPedaVariable(metadata, 'peda_shields_max')}
              onChange={(e) =>
                onChange(patchPedaVariable(metadata, 'peda_shields_max', Math.max(0, parseInt(e.target.value, 10) || 0)))
              }
            />
          </div>
        </div>
      </div>
      {Object.keys(getOtherInitialVariables(metadata)).length > 0 ? (
        <div className={styles.field}>
          <label>Autres variables initiales (JSON)</label>
          <textarea
            className={styles.textarea}
            rows={3}
            value={JSON.stringify(getOtherInitialVariables(metadata), null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value) as Record<string, number>;
                patch({
                  initialVariables: {
                    ...parsed,
                    peda_shields: getPedaVariable(metadata, 'peda_shields'),
                    peda_shields_max: getPedaVariable(metadata, 'peda_shields_max'),
                  },
                });
              } catch { /* ignore while typing */ }
            }}
          />
        </div>
      ) : null}

      <TextPlaceholdersEditor />

      <MissionsEditor
        scenarioId={scenarioId}
        missions={metadata.missions ?? []}
        chapters={metadata.chapters ?? []}
        scenes={scenes}
        onChange={(missions) => patch({ missions: missions.length > 0 ? missions : undefined })}
      />

      <ChaptersEditor
        scenarioId={scenarioId}
        chapters={metadata.chapters ?? []}
        sceneIds={sceneIds}
        missionIds={missionIds}
        onChange={(chapters) => patch({ chapters: chapters.length > 0 ? chapters : undefined })}
      />

      <AchievementsEditor
        scenarioId={scenarioId}
        achievements={metadata.achievements}
        onChange={(achievements) => patch({ achievements })}
      />

      <SkillsEditor
        scenarioId={scenarioId}
        skills={metadata.skills ?? []}
        onChange={(skills) => patch({ skills: skills.length > 0 ? skills : undefined })}
      />
    </div>
  );
};

interface ItemsEditorProps {
  items: Scenario['items'];
  scenarioId: string;
  onChange: (items: Scenario['items']) => void;
}

export const ItemsEditor: React.FC<ItemsEditorProps> = ({ items, onChange }) => (
  <div className={styles.panel}>
    <h3>Items inventaire</h3>
    <JsonEditor label="items" value={items} onChange={onChange} />
  </div>
);
